import React, { RefObject } from 'react';
import classNames from 'classnames';
import Uppy from '@uppy/core';
import getDroppedFiles from '@uppy/utils/lib/getDroppedFiles';

import styles from './DragDrop.scss';

type Props = {
  inputName: string;
  maxNumberOfFiles: number;
  allowedFileTypes?: string[];
  uppy: Uppy;
  isDisabled?: boolean;
  children: React.ReactNode;
  onBeforeAddFiles?: () => void;
};

type State = {
  isDragging: boolean;
};

class DragDrop extends React.PureComponent<Props, State> {
  private readonly inputRef: RefObject<HTMLInputElement>;

  state = {
    isDragging: false,
  };

  constructor(props: Props) {
    super(props);
    this.inputRef = React.createRef<HTMLInputElement>();
  }

  onButtonClick = () => this.inputRef.current?.click();

  addFile(file: File) {
    const { uppy } = this.props;
    try {
      uppy.addFile({
        source: 'uploader-drag-drop',
        name: file.name,
        type: file.type,
        data: file,
      });
    } catch (err) {
      // unfortunately current version of uppy doesn't export RestrictionError type
      // @ts-ignore
      if (!err.isRestriction) {
        uppy.log(err instanceof Error ? err.message : 'Unknown error');
      }
    }
  }

  addFiles(files: File[]) {
    files.forEach((file) => this.addFile(file));
  }

  onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    this.props.onBeforeAddFiles && this.props.onBeforeAddFiles();

    this.addFiles([...e.target.files]); // convert FileList to Array
    // @ts-ignore
    e.target.value = null; // workaround like in original "@uppy/drag-drop"
  };

  onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    this.setState({ isDragging: true });

    e.preventDefault();
    e.stopPropagation();
  };

  onDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
    this.setState({ isDragging: false });

    e.preventDefault();
    e.stopPropagation();
  };

  onDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    const { onBeforeAddFiles } = this.props;
    onBeforeAddFiles && onBeforeAddFiles();

    this.setState({ isDragging: false });

    getDroppedFiles(e.dataTransfer).then((files) => this.addFiles(files));

    e.preventDefault();
    e.stopPropagation();
  };

  render() {
    const { inputName, maxNumberOfFiles, allowedFileTypes, isDisabled, uppy } = this.props;

    if (!uppy) {
      return;
    }

    const { isDragging } = this.state;

    return (
      <button
        className={classNames(styles.dropZone, { [styles.dragging]: isDragging, [styles.disabled]: isDisabled })}
        type="button"
        disabled={isDisabled}
        onDragOver={this.onDragOver}
        onDragLeave={this.onDragLeave}
        onDrop={isDisabled ? undefined : this.onDrop}
        onClick={this.onButtonClick}
      >
        <input
          ref={this.inputRef}
          className={styles.input}
          type="file"
          tabIndex={-1}
          name={inputName}
          multiple={maxNumberOfFiles !== 1}
          accept={allowedFileTypes?.join(',')}
          onChange={this.onInputChange}
        />
        {this.props.children}
      </button>
    );
  }
}

export default DragDrop;
